import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
    /** Libellé de la zone protégée (ex: nom de la page) pour contextualiser l'erreur. */
    zone?: string;
    /** Fallback minimal sans chrome (pour les petites zones). */
    compact?: boolean;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error Boundary : capture les erreurs de rendu des enfants et affiche
 * un écran de repli au lieu d'une page blanche.
 *
 * - Au niveau racine (index.tsx) : protège toute l'application.
 * - Au niveau AppLayout : une page qui plante ne détruit pas le shell
 *   (sidebar/topbar restent, l'utilisateur peut naviguer ailleurs).
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error(`[ErrorBoundary${this.props.zone ? ' - ' + this.props.zone : ''}]`, error, info.componentStack);
    }

    private reset = () => this.setState({ hasError: false, error: undefined });

    render() {
        if (!this.state.hasError) return this.props.children;

        if (this.props.compact) {
            return (
                <div className="flex flex-col items-center justify-center gap-3 p-8 rounded-xl border border-[var(--eb-border)] bg-[var(--eb-surface)] text-center">
                    <AlertTriangle size={28} className="text-amber-500" />
                    <p className="text-sm text-[var(--eb-text-muted)]">
                        Une erreur est survenue{this.props.zone ? ` dans « ${this.props.zone} »` : ''}.
                    </p>
                    <button
                        onClick={this.reset}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[var(--eb-primary)] text-white hover:opacity-90"
                    >
                        <RefreshCw size={13} /> Réessayer
                    </button>
                </div>
            );
        }

        return (
            <div className="min-h-[60vh] flex items-center justify-center p-6">
                <div className="max-w-md w-full text-center bg-[var(--eb-surface)] border border-[var(--eb-border)] rounded-2xl p-8">
                    <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                        <AlertTriangle size={28} className="text-amber-600" />
                    </div>
                    <h2 className="text-lg font-bold text-[var(--eb-text)] mb-1">
                        Une erreur est survenue
                    </h2>
                    <p className="text-sm text-[var(--eb-text-muted)] mb-1">
                        {this.props.zone ? `La section « ${this.props.zone} » a rencontré un problème.` : 'L\'application a rencontré un problème inattendu.'}
                    </p>
                    {this.state.error?.message && (
                        <p className="text-xs text-[var(--eb-text-muted)]/70 font-mono bg-[var(--eb-bg)] rounded-lg px-3 py-2 my-3 break-all">
                            {this.state.error.message}
                        </p>
                    )}
                    <div className="flex gap-2 justify-center mt-5">
                        <button
                            onClick={this.reset}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-[var(--eb-border)] text-[var(--eb-text)] hover:bg-[var(--eb-bg)]"
                        >
                            <RefreshCw size={15} /> Réessayer
                        </button>
                        <button
                            onClick={() => { this.reset(); window.location.href = '/'; }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-[var(--eb-primary)] text-white hover:opacity-90"
                        >
                            <Home size={15} /> Accueil
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
